import React from "react";
import { Table, Tag, Typography } from "antd";
import type { TableColumnsType } from "antd";
import type {
  CourseStatusChange,
  DocOutcome,
  HubRow,
  QuizOutcome,
  RowRun,
} from "./hubTypes";
import type { PartDisplay, RowView } from "./rowState";

export interface RowItem {
  row: HubRow;
  run: RowRun | undefined;
  view: RowView;
}

const PART_TAGS: Record<
  Exclude<PartDisplay, "none">,
  { label: string; color: string }
> = {
  pending: { label: "Pendiente", color: "default" },
  running: { label: "En curso", color: "processing" },
  done: { label: "Subido", color: "success" },
  blocked: { label: "Bloqueado", color: "error" },
  checking: { label: "Validando…", color: "processing" },
  valid: { label: "Válido", color: "cyan" },
  uploading: { label: "Subiendo…", color: "processing" },
  error: { label: "Con error", color: "error" },
  cancelled: { label: "Cancelado", color: "warning" },
};

// SCRUM-1532, screen 2: how a run left each part, when it says more than the
// part's status.
const DOC_OUTCOMES: Record<DocOutcome, string> = {
  replaced: "Sustituida",
  created: "Subida",
  structure: "No subida: revisa la estructura",
  cancelled: "Cancelada: cambió en plataforma",
};

const QUIZ_OUTCOMES: Record<QuizOutcome, string> = {
  replaced: "Sustituido",
  created: "Subido",
  blocked: "Bloqueado: el quiz ya tiene respuestas de alumnos",
  cancelled: "Cancelado: cambió en plataforma",
};

const COURSE_STATUS: Record<CourseStatusChange, string> = {
  finalized: "Revisada → Finalizada",
  stillPorRevisar: "Por revisar no cambia",
  unchanged: "sin cambio",
};

const NONE = <Typography.Text type="secondary">—</Typography.Text>;

// «Sin cambios» when something was replaced in place, the new resource when a
// quiz was created, «—» otherwise.
function resourceIdOf(run: RowRun | undefined): React.ReactNode {
  if (run?.quizOutcome === "created" && run.quizResourceId !== undefined) {
    return run.quizResourceId;
  }
  if (run?.docOutcome === "replaced" || run?.quizOutcome === "replaced") {
    return "Sin cambios";
  }
  return NONE;
}

interface PartCellProps {
  status: PartDisplay;
  url: string | null;
  progress?: number;
  outcome?: string;
}

const PartCell: React.FC<PartCellProps> = ({
  status,
  url,
  progress,
  outcome,
}) => {
  if (status === "none") return NONE;
  const { label, color } = PART_TAGS[status];
  const text =
    status === "uploading" && progress !== undefined
      ? `${label} ${progress}%`
      : (outcome ?? label);
  const tag = <Tag color={color}>{text}</Tag>;
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {tag}
    </a>
  ) : (
    tag
  );
};

const columns: TableColumnsType<RowItem> = [
  {
    title: "Fila",
    key: "rowNumber",
    width: 70,
    render: (_, { row }) => row.rowNumber,
  },
  {
    title: "ID",
    key: "courseId",
    width: 80,
    render: (_, { row }) => row.courseId ?? "—",
  },
  {
    title: "Curso",
    key: "courseTitle",
    render: (_, { row }) => row.courseTitle,
  },
  {
    title: "Clave",
    key: "clave",
    width: 140,
    render: (_, { row }) => row.clave,
  },
  {
    title: "OD",
    key: "doc",
    width: 160,
    render: (_, { row, run, view }) => (
      <PartCell
        status={view.doc}
        url={row.docUrl}
        progress={run?.docProgress}
        outcome={run?.docOutcome && DOC_OUTCOMES[run.docOutcome]}
      />
    ),
  },
  {
    title: "Quiz",
    key: "quiz",
    width: 160,
    render: (_, { row, run, view }) => (
      <PartCell
        status={view.quiz}
        url={row.yamlUrl}
        outcome={run?.quizOutcome && QUIZ_OUTCOMES[run.quizOutcome]}
      />
    ),
  },
  {
    title: "ID de recurso",
    key: "resourceId",
    width: 110,
    render: (_, { run }) => resourceIdOf(run),
  },
  {
    title: "Estado del curso",
    key: "courseStatus",
    width: 160,
    render: (_, { run }) =>
      run?.courseStatus ? COURSE_STATUS[run.courseStatus] : NONE,
  },
  {
    title: "Errores",
    key: "errors",
    render: (_, { run, view }) => (
      <>
        {view.messages.length > 0 ? (
          <Typography.Text
            type="danger"
            style={{ display: "block", whiteSpace: "pre-line" }}
          >
            {view.messages.join("\n")}
          </Typography.Text>
        ) : null}
        {view.notices.length > 0 ? (
          <Typography.Text
            type="warning"
            style={{ display: "block", whiteSpace: "pre-line" }}
          >
            {view.notices.join("\n")}
          </Typography.Text>
        ) : null}
        {run?.note ? (
          <Typography.Text type="secondary" style={{ display: "block" }}>
            {run.note}
          </Typography.Text>
        ) : null}
      </>
    ),
  },
];

interface Props {
  items: RowItem[];
  loading: boolean;
  emptyText?: string;
}

const RowsTable: React.FC<Props> = ({
  items,
  loading,
  emptyText = "No hay filas pendientes en esta pestaña.",
}) => (
  <Table<RowItem>
    size="small"
    rowKey={(item) => item.row.rowNumber}
    columns={columns}
    dataSource={items}
    loading={loading}
    scroll={{ x: 1300 }}
    pagination={{
      pageSize: 50,
      showSizeChanger: false,
      hideOnSinglePage: true,
    }}
    locale={{ emptyText }}
  />
);

export default RowsTable;
